import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import styled, { css } from 'styled-components';

import useBlurDataUrl from 'hooks/useBlurDataUrl';

interface IImage {
	src: string;
	alt: string;
	_width?: number;
	_height?: number;
	error_src?: string;
	lazy?: boolean;
}

const CoreImg = (props: IImage) => {
	const { src, error_src, lazy } = props;
	// loading-image-hook
	const [shimmer, toBase64] = useBlurDataUrl();
	const loading_src = `data:image/svg+xml;base64,${toBase64(shimmer())}`;

	// 이미지 경로
	const [img_src, setImgSrc] = useState(loading_src || src);

	// 화면 노출 여부
	const img_ref = useRef<HTMLImageElement>(null);
	const observer_ref = useRef<IntersectionObserver>();
	const [is_loaded, setIsLoaded] = useState(!lazy);

	const onIntersection = (
		entries: IntersectionObserverEntry[],
		io: IntersectionObserver,
	) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				io.unobserve(entry.target);
				setIsLoaded(true);
			}
		});
	};

	useEffect(() => {
		if (!lazy) return;

		if (!observer_ref.current) {
			observer_ref.current = new IntersectionObserver(onIntersection);
		}
		img_ref.current && observer_ref.current.observe(img_ref.current);
	}, [img_ref]);

	/** 이미지가 로드되거나, 에러가 나면 상태에 맞는 이미지로 경로를 변경 */
	useEffect(() => {
		if (src === undefined || !is_loaded) return;
		const img = new Image();
		img.src = src;
		img.onload = () => {
			setImgSrc(src);
		};
		img.onerror = () => {
			setImgSrc(error_src ?? '');
		};
	}, [src, is_loaded]);

	return (
		<ProgressiveImg
			is_loading={img_src === loading_src}
			{...{ ...props, src: img_src }}
			ref={img_ref}
		/>
	);
};

const ProgressiveImg = styled.img<{
	is_loading: boolean;
	_width?: number;
	_height?: number;
}>`
	width: ${({ _width }) => (_width ? _width + 'px' : '100%')};
	height: ${({ _height }) => (_height ? _height + 'px' : '100%')};

	object-fit: cover;
	object-position: center;

	${({ is_loading }) =>
		is_loading
			? css`
					filter: blur(10px);
					clip-path: inset(0);
			  `
			: css`
					filter: blur(0px);
					transition: filter 0.2s linear;
			  `}
`;

export default CoreImg;
